import { createElement, forwardRef, useMemo } from 'react';
import { postfixTestId } from '../../util/testId';
import { MenuItem } from '../Menu';
import { TokenDesign } from './TokenDesign';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-Token-gui-icu.properties';
import { GroupTokenProps } from './types';
import { useFormatter } from '../../locale/formatting';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const GroupToken = forwardRef<HTMLDivElement, GroupTokenProps>((props, ref) => {
  const localize = useTextLocalizer();
  const groupTokenValues = props.values;
  const showMoreThreshold =
    props.showMoreThreshold && props.showMoreThreshold < 5 && props.showMoreThreshold > 0
      ? 5
      : props.showMoreThreshold && props.showMoreThreshold < 100 && props.showMoreThreshold > 0
      ? props.showMoreThreshold
      : 100;
  const showMoreItem =
    (groupTokenValues.length > 5 && showMoreThreshold < groupTokenValues.length) ||
    groupTokenValues.length > 100;

  const menuItems: MenuItem[] = useMemo(
    () =>
      groupTokenValues.slice(0, showMoreThreshold).map((value) => {
        return {
          testId: postfixTestId(props.testId, value),
          text: value,
          closable: !props.readOnly,
          closeIconAriaLabel: localize('menuButton.menuItem.closeIcon.default.aria'),
          closeIconTooltip: localize('menuButton.menuItem.closeIcon.default.tooltip.label'),
          closableAriaText: localize('menuButton.menuItem.delete.aria'),
        };
      }),

    [groupTokenValues, showMoreThreshold, props.testId, props.readOnly, localize]
  );

  const formatter = useFormatter();
  const formattedCount =
    formatter?.formatNumeric(groupTokenValues.length) ?? groupTokenValues.length;
  const label = `${props.label} (${formattedCount})`;

  return (
    <div className={props.className} style={props.style}>
      <TokenDesign
        testId={props.testId}
        variant={props.variant}
        readOnly={props.readOnly}
        tabIndex={props.tabIndex}
        onClose={props.onClose}
        onFocus={props.onFocus}
        ref={ref}
        value={label}
        ariaLabel={props.ariaLabel}
        ariaDescribedBy={props.ariaDescribedBy}
        onClick={props.onClick}
        includeDropdown
        menuSections={
          showMoreItem
            ? [
                {
                  items: [
                    {
                      testId: postfixTestId(props.testId, '-moreMenuItem'),
                      text: localize('groupToken.menuItem.moreMenuItem.label'),
                      closable: false,
                    },
                  ],
                },
                { items: menuItems },
              ]
            : [{ items: menuItems }]
        }
        onItemSelected={([itemLocation]) => {
          const [sectionIndex, itemIndex] = itemLocation;
          if (showMoreItem && sectionIndex === 0 && itemIndex === 0) {
            props.onMorePress?.();
          }
        }}
        onCloseItem={props.onCloseItem}
        onTokenFocusBlur={props.onTokenFocusBlur}
        keyInteraction={props.keyInteraction}
        hasTokenCountChanged={props.hasTokenCountChanged}
        isFirstToken={props.isFirstToken}
        isLastToken={props.isLastToken}
        closeButtonProps={props.closeButtonProps}
      />
    </div>
  );
});
